import { collection, onSnapshot, doc, setDoc, serverTimestamp, type DocumentData, type QueryDocumentSnapshot } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from './firebase';

export type StripeBillingCycle = 'monthly' | 'yearly';
export type StripePlanId = 'vital' | 'lifetime' | 'starter';

// Firestore collection that holds billing subscription docs.
const CUSTOMERS_COLLECTION = 'customers';

// ── Lemon Squeezy checkout ────────────────────────────────────────────────────
// Vital and Lifetime each have ONE product checkout URL; monthly vs yearly is
// selected by appending ?variant={numericId} to the URL.
// Starter is a single variant ($0, 15-day trial → auto-converts to Vital Monthly).
const LS_CHECKOUT_STARTER  = import.meta.env.VITE_LS_CHECKOUT_STARTER  ?? '';
const LS_CHECKOUT_PETSITTERS = import.meta.env.VITE_LS_CHECKOUT_PETSITTERS ?? '';
const LS_CHECKOUT_VITAL    = import.meta.env.VITE_LS_CHECKOUT_VITAL    ?? '';
const LS_CHECKOUT_LIFETIME = import.meta.env.VITE_LS_CHECKOUT_LIFETIME ?? '';
const LS_DISCOUNT_PETSITTERS_FIRST30 = 'SITTERFIRST30';
const LS_STORE_DOMAIN = (import.meta.env.VITE_LS_STORE_DOMAIN || 'kodacare.lemonsqueezy.com').trim().toLowerCase();
const APP_ENV = import.meta.env.MODE || 'unknown';

// Numeric variant IDs — used to pre-select monthly or yearly on the checkout page.
const LS_VARIANT_VITAL_MONTHLY    = import.meta.env.VITE_LS_VARIANT_VITAL_MONTHLY    ?? '';
const LS_VARIANT_VITAL_YEARLY     = import.meta.env.VITE_LS_VARIANT_VITAL_YEARLY     ?? '';
const LS_VARIANT_LIFETIME_MONTHLY = import.meta.env.VITE_LS_VARIANT_LIFETIME_MONTHLY ?? '';
const LS_VARIANT_LIFETIME_YEARLY  = import.meta.env.VITE_LS_VARIANT_LIFETIME_YEARLY  ?? '';

const LS_CHECKOUT_BASES: Record<Exclude<StripePlanId, 'starter'>, string> = {
  vital:    normaliseCheckoutBase(LS_CHECKOUT_VITAL),
  lifetime: normaliseCheckoutBase(LS_CHECKOUT_LIFETIME),
};
const LS_VARIANT_IDS: Record<Exclude<StripePlanId, 'starter'>, Record<StripeBillingCycle, string>> = {
  vital:    { monthly: LS_VARIANT_VITAL_MONTHLY,    yearly: LS_VARIANT_VITAL_YEARLY    },
  lifetime: { monthly: LS_VARIANT_LIFETIME_MONTHLY, yearly: LS_VARIANT_LIFETIME_YEARLY },
};

const APP_HOST = import.meta.env.VITE_APP_HOST || 'https://app.kodacare.pet';

function normaliseCheckoutBase(rawUrl?: string | null): string {
  if (!rawUrl) return '';
  try {
    const parsed = new URL(rawUrl);
    // Keep the checkout path/id, but always force the configured LS store domain.
    return `https://${LS_STORE_DOMAIN}${parsed.pathname}`;
  } catch {
    return '';
  }
}

function normaliseCampaign(value?: string | null): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  // Keep URL-safe campaign ids only.
  const safe = trimmed.replace(/[^a-zA-Z0-9._-]/g, '').slice(0, 64);
  return safe || null;
}

function campaignSegment(campaign: string | null): string | null {
  if (!campaign) return null;
  if (campaign.startsWith('petsitters')) return 'pet_sitter';
  return null;
}

/**
 * Returns the Lemon Squeezy Starter checkout URL for new user registration.
 * Collects a payment method upfront for the 15-day $0 trial;
 * LS auto-upgrades to Vital Companion Monthly when the trial ends.
 * After checkout, LS redirects to `${APP_HOST}/verify-email`.
 */
export function getStarterCheckoutUrl(options: { uid: string; email: string; name?: string; campaign?: string | null; redirectUrl?: string }): string | null {
  const campaign = normaliseCampaign(options.campaign);
  const segment = campaignSegment(campaign);
  const isPetSitterCampaign = campaign === 'petsitters-first30';
  const checkoutBase = isPetSitterCampaign
    ? normaliseCheckoutBase(LS_CHECKOUT_PETSITTERS || LS_CHECKOUT_STARTER)
    : normaliseCheckoutBase(LS_CHECKOUT_STARTER);

  if (!checkoutBase) return null;

  const params = new URLSearchParams();
  params.set('checkout[custom][firebase_uid]', options.uid);
  params.set('checkout[custom][app_env]', APP_ENV);
  params.set('checkout[email]', options.email);
  if (options.name) params.set('checkout[name]', options.name);
  params.set('checkout[redirect_url]', options.redirectUrl || `${APP_HOST}/verify-email`);

  if (campaign) params.set('checkout[custom][campaign]', campaign);
  if (segment) params.set('checkout[custom][segment]', segment);
  if (isPetSitterCampaign && LS_VARIANT_LIFETIME_YEARLY) {
    params.set('variant', LS_VARIANT_LIFETIME_YEARLY);
  }
  if (isPetSitterCampaign) params.set('checkout[custom][offer]', 'first_30_free');
  if (isPetSitterCampaign && LS_DISCOUNT_PETSITTERS_FIRST30) {
    params.set('checkout[discount_code]', LS_DISCOUNT_PETSITTERS_FIRST30);
  }

  return `${checkoutBase}?${params.toString()}`;
}

/**
 * Returns a Lemon Squeezy checkout URL for the given plan/cycle.
 * Appends `?variant={id}` to pre-select monthly or yearly, plus
 * `checkout[custom][firebase_uid]` so the webhook can link the
 * subscription back to the Firebase user.
 */
export function getCheckoutUrl(
  plan: Exclude<StripePlanId, 'starter'>,
  cycle: StripeBillingCycle,
  options?: { uid?: string; email?: string; redirectUrl?: string },
): string | null {
  const base      = LS_CHECKOUT_BASES[plan];
  const variantId = LS_VARIANT_IDS[plan]?.[cycle];
  if (!base || !variantId) return null;

  const params = new URLSearchParams();
  params.set('variant', variantId);
  params.set('checkout[custom][app_env]', APP_ENV);
  if (options?.uid)   params.set('checkout[custom][firebase_uid]', options.uid);
  if (options?.email) params.set('checkout[email]', options.email);
  if (options?.redirectUrl) params.set('checkout[redirect_url]', options.redirectUrl);

  return `${base}?${params.toString()}`;
}

export interface StripeSubscriptionSummary {
  id: string;
  planName?: string;
  /** Normalised plan tier written by the webhook: 'vital' | 'lifetime' | null */
  planId?: string | null;
  status?: string;
  startedAt?: string;
  nextPaymentAt?: string;
  expiresAt?: string;
  cancelAtPeriodEnd: boolean;
  raw: Record<string, unknown>;
}

type TimestampLike =
  | string
  | number
  | Date
  | { seconds: number; nanoseconds?: number }
  | { toDate: () => Date }
  | null
  | undefined;

function asDate(value: TimestampLike): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  if (typeof value === 'object' && 'toDate' in value && typeof value.toDate === 'function') {
    return value.toDate();
  }
  if (typeof value === 'object' && 'seconds' in value && typeof value.seconds === 'number') {
    return new Date(value.seconds * 1000);
  }
  return null;
}

function toIsoString(value: TimestampLike): string | undefined {
  return asDate(value)?.toISOString();
}

function getPrimaryPrice(data: Record<string, any>) {
  if (Array.isArray(data.prices) && data.prices.length > 0) return data.prices[0];
  if (data.price && typeof data.price === 'object') return data.price;
  // Webhook stores items: [{ price: { ... , product: { id, name } } }]
  if (Array.isArray(data.items) && data.items.length > 0) return data.items[0]?.price;
  return undefined;
}

function getPlanName(data: Record<string, any>): string | undefined {
  const primaryPrice = getPrimaryPrice(data);
  const candidate =
    data.role ??
    data.metadata?.planName ??
    data.metadata?.plan ??
    primaryPrice?.product?.name ??
    primaryPrice?.nickname ??
    data.items?.[0]?.price?.product?.name ??
    data.items?.[0]?.price?.nickname;

  return typeof candidate === 'string' && candidate.trim() ? candidate : undefined;
}

function statusRank(status?: string): number {
  switch ((status ?? '').toLowerCase()) {
    case 'active':
      return 0;
    case 'on_trial':
    case 'trialing':
      return 1;
    case 'past_due':
      return 2;
    case 'unpaid':
      return 3;
    case 'incomplete':
      return 4;
    case 'canceled':
      return 5;
    default:
      return 6;
  }
}

function mapSubscriptionDoc(docSnap: QueryDocumentSnapshot<DocumentData>): StripeSubscriptionSummary {
  const data = docSnap.data() as Record<string, any>;
  const periodStart = data.current_period_start;
  const periodEnd = data.current_period_end;
  const canceledAt = data.ended_at ?? data.cancel_at ?? data.canceled_at;

  const planName = getPlanName(data);

  // Read stored planId first, then derive client-side from planName as a fallback.
  // This means pre-existing docs without a planId field still work correctly.
  const storedPlanId: string | null =
    typeof data.planId === 'string' ? data.planId : (data.metadata?.planId ?? null);
  const nameLower = (planName ?? '').toLowerCase();
  const derivedPlanId: string | null = storedPlanId
    ?? ((nameLower.includes('lifetime') || nameLower.includes('expert companion')) ? 'lifetime'
      : nameLower.includes('vital') ? 'vital'
      : null);

  return {
    id: docSnap.id,
    planName,
    planId: derivedPlanId,
    status: typeof data.status === 'string' ? data.status : undefined,
    startedAt: toIsoString(data.created ?? periodStart),
    nextPaymentAt: toIsoString(periodEnd),
    expiresAt: toIsoString(data.cancel_at_period_end ? periodEnd : canceledAt ?? periodEnd),
    cancelAtPeriodEnd: Boolean(data.cancel_at_period_end),
    raw: data,
  };
}

/** Higher-tier plans win when status is equal (lifetime > vital > anything else). */
function planTierRank(sub: StripeSubscriptionSummary): number {
  // Prefer the normalised planId written by the webhook
  const id = (sub.planId ?? '').toLowerCase();
  if (id === 'lifetime') return 0;
  if (id === 'vital') return 1;
  // Fall back to planName string matching
  const name = (sub.planName ?? '').toLowerCase();
  if (name.includes('lifetime') || name.includes('expert companion')) return 0;
  if (name.includes('vital')) return 1;
  return 2;
}

function pickBestSubscription(subscriptions: StripeSubscriptionSummary[]): StripeSubscriptionSummary | null {
  if (!subscriptions.length) return null;

  return [...subscriptions].sort((left, right) => {
    const byStatus = statusRank(left.status) - statusRank(right.status);
    if (byStatus !== 0) return byStatus;

    // Same status — prefer the higher-tier plan (e.g. Lifetime over Vital)
    const byTier = planTierRank(left) - planTierRank(right);
    if (byTier !== 0) return byTier;

    const leftDate = asDate(left.nextPaymentAt)?.getTime() ?? 0;
    const rightDate = asDate(right.nextPaymentAt)?.getTime() ?? 0;
    return rightDate - leftDate;
  })[0] ?? null;
}

export function listenToCurrentSubscription(
  uid: string,
  onChange: (subscription: StripeSubscriptionSummary | null, all: StripeSubscriptionSummary[]) => void,
): () => void {
  if (!uid || !CUSTOMERS_COLLECTION) {
    onChange(null, []);
    return () => {};
  }
  return onSnapshot(
    collection(db, CUSTOMERS_COLLECTION, uid, 'subscriptions'),
    (snapshot) => {
      const subscriptions = snapshot.docs.map(mapSubscriptionDoc);
      onChange(pickBestSubscription(subscriptions), subscriptions);
    },
    (error) => {
      console.error('[StripeBilling] Failed to read subscriptions:', error);
      onChange(null, []);
    },
  );
}

export async function triggerLsCheckoutSync(orderId: string): Promise<void> {
  const auth = getAuth();
  const uid = auth.currentUser?.uid;
  if (!uid || !orderId) return;

  await setDoc(doc(db, 'pendingLsCheckouts', orderId), {
    uid,
    orderId,
    createdAt: serverTimestamp(),
  });
}


